import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  Link,
  useNavigate,
  useParams
} from 'react-router-dom';

import './AdminPartnerDetail.scss';

export default function AdminPartnerDetail() {
  const {
    id
  } = useParams();

  const navigate =
    useNavigate();

  const apiBaseUrl =
    useMemo(() => {
      const envUrl =
        process.env
          .REACT_APP_API_URL ||
        '';

      return envUrl.endsWith('/')
        ? envUrl.slice(
            0,
            -1
          )
        : envUrl;
    }, []);

  const token =
    localStorage.getItem(
      'tg_admin_token'
    );

  const [
    partner,
    setPartner
  ] = useState(null);

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    deleting,
    setDeleting
  ] = useState(false);

  const [
    error,
    setError
  ] = useState('');

  useEffect(() => {
    const fetchPartner =
      async () => {
        try {
          const response =
            await fetch(
              `${apiBaseUrl}/admin/partners/${id}`,
              {
                headers: {
                  Authorization:
                    `Bearer ${token}`
                }
              }
            );

          const data =
            await response.json();

          if (!response.ok) {
            throw new Error(
              data?.message ||
                'Unable to load partner.'
            );
          }

          setPartner(
            data.partner
          );
        } catch (err) {
          setError(
            err.message ||
              'Unable to load partner.'
          );
        } finally {
          setLoading(false);
        }
      };

    fetchPartner();
  }, [
    apiBaseUrl,
    id,
    token
  ]);

  const formatDate = (
    value
  ) => {
    if (!value) {
      return '—';
    }

    return new Date(
      value
    ).toLocaleDateString(
      undefined,
      {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      }
    );
  };

  const deletePartner =
    async () => {
      const confirmed =
        window.confirm(
          `Delete "${partner.organization_name}" from the partner CRM?`
        );

      if (!confirmed) {
        return;
      }

      setDeleting(true);

      try {
        const response =
          await fetch(
            `${apiBaseUrl}/admin/partners/${id}`,
            {
              method:
                'DELETE',

              headers: {
                Authorization:
                  `Bearer ${token}`
              }
            }
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data?.message ||
              'Unable to delete partner.'
          );
        }

        navigate(
          '/admin/partners'
        );
      } catch (err) {
        setError(
          err.message ||
            'Unable to delete partner.'
        );
      } finally {
        setDeleting(false);
      }
    };

  if (loading) {
    return (
      <div className="admin-partner-detail-loading">
        Loading partner...
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="admin-partner-detail-loading">
        {error ||
          'Partner not found.'}
      </div>
    );
  }

  return (
    <div className="admin-partner-detail">

      <section className="admin-partner-detail__heading">

        <div>
          <p>
            Partner CRM
          </p>

          <h2>
            {
              partner.organization_name
            }
          </h2>

          <span>
            {partner.partner_type}
            {' · '}
            {partner.status}
          </span>
        </div>

        <div className="admin-partner-detail__actions">

          <Link
            to={`/admin/partners/${id}/edit`}
            className="primary"
          >
            Edit Partner
          </Link>

          <button
            type="button"
            onClick={() =>
              navigate(
                '/admin/partners'
              )
            }
          >
            Back
          </button>

        </div>

      </section>

      {error ? (
        <div className="admin-partner-detail__error">
          {error}
        </div>
      ) : null}

      <section className="admin-partner-detail-metrics">

        <div>
          <span>
            Status
          </span>

          <strong>
            {partner.status}
          </strong>
        </div>

        <div>
          <span>
            Type
          </span>

          <strong>
            {
              partner.partner_type
            }
          </strong>
        </div>

        <div>
          <span>
            Last Contact
          </span>

          <strong>
            {formatDate(
              partner.last_contact_at
            )}
          </strong>
        </div>

        <div>
          <span>
            Next Follow-Up
          </span>

          <strong>
            {formatDate(
              partner.next_follow_up_at
            )}
          </strong>
        </div>

      </section>

      <section className="admin-partner-detail-card">

        <div className="admin-partner-detail-card__header">
          <h3>
            Contact
          </h3>
        </div>

        <div className="admin-partner-detail-grid">

          <div>
            <span>
              Name
            </span>

            <strong>
              {partner.contact_name ||
                '—'}
            </strong>
          </div>

          <div>
            <span>
              Title
            </span>

            <strong>
              {partner.contact_title ||
                '—'}
            </strong>
          </div>

          <div>
            <span>
              Email
            </span>

            <strong>
              {partner.contact_email ||
                '—'}
            </strong>
          </div>

          <div>
            <span>
              Phone
            </span>

            <strong>
              {partner.contact_phone ||
                '—'}
            </strong>
          </div>

        </div>

      </section>

      <section className="admin-partner-detail-card">

        <div className="admin-partner-detail-card__header">
          <h3>
            Relationship
          </h3>
        </div>

        <div className="admin-partner-detail-grid">

          <div>
            <span>
              Related Course
            </span>

            <strong>
              {partner.course_name ||
                '—'}
            </strong>
          </div>

          <div>
            <span>
              Website
            </span>

            <strong>
              {partner.website ||
                '—'}
            </strong>
          </div>

          <div>
            <span>
              Created
            </span>

            <strong>
              {formatDate(
                partner.created_at
              )}
            </strong>
          </div>

          <div>
            <span>
              Updated
            </span>

            <strong>
              {formatDate(
                partner.updated_at
              )}
            </strong>
          </div>

        </div>

      </section>

      <section className="admin-partner-notes">

        <div className="admin-partner-notes__header">
          <h3>
            Notes
          </h3>
        </div>

        <div className="admin-partner-notes__body">
          {partner.notes ? (
            <p>
              {partner.notes}
            </p>
          ) : (
            <span>
              No notes added.
            </span>
          )}
        </div>

      </section>

      <section className="admin-partner-danger">

        <div>
          <strong>
            Delete CRM Record
          </strong>

          <span>
            This removes the
            relationship record but
            does not delete the
            linked course.
          </span>
        </div>

        <button
          type="button"
          onClick={
            deletePartner
          }
          disabled={
            deleting
          }
        >
          {deleting
            ? 'Deleting...'
            : 'Delete Partner'}
        </button>

      </section>

    </div>
  );
}